/**
 * One ordered, relative-timestamped timeline for the whole local ASR lifecycle.
 *
 * Replaces the per-event S3 objects the lifecycle used to emit (`local_asr_init_success`,
 * `localAsr`/`event`, `asrAssetCache`/`event`, ...). Those were readable only by sorting
 * S3 filenames by their wall-clock suffix, carried no durations, and — because most of
 * them fire before an activity exists — landed under the hex-of-userId key rather than
 * the activity, so the pre-activity story and the in-activity story lived in two
 * different folders with no way to line them up.
 *
 * Events accumulate in memory with an offset from the first recorded event and are
 * written as a single `local_asr_lifecycle` object at checkpoints (see
 * flushLocalAsrLifecycle). Flushing at activity start re-emits the accumulated
 * pre-activity timeline under the activity key, so one object answers "what happened
 * before this activity, and what did local ASR do during it".
 *
 * Event names are dot-namespaced by phase (`enablement.*`, `assets.*`, `runtime.*`,
 * `graphs.*`, `decode.*`) so a single timeline stays scannable. Per-phrase decode
 * stats (`decode.phraseStats`) scale with phrase count and accumulate in the same
 * session object — fine for typical sessions, but S3 payload size grows with
 * phrases × activities rather than staying roughly fixed.
 *
 * TT-1151 added `decode.interventionStats`, one per intervention, and multiple
 * interventions per phrase is ordinary — so the dominant term of that payload is now
 * plausibly 2-3x what it was, on the slow devices the payload exists to diagnose. An
 * unpublished-graph story adds one scoped `graphs.phraseFallback` per phrase-with-an-
 * intervention on top. Not sized against a real cohort yet; if the flushed object gets
 * unwieldy, per-mode sampling or dropping all-zero intervention records are the levers.
 */

import { logSessionInfo } from '../logger';

/** S3 log type for the flushed timeline. */
export const LOCAL_ASR_LIFECYCLE_LOG_TYPE = 'local_asr_lifecycle';

// Re-exported from its dependency-free leaf so jest.mock factories replacing THIS
// module can requireActual the vocabulary without dragging in the ../logger chain
// above — see localAsrFallbackReason.js for the full rationale. Consumers keep
// importing it from here.
export { LOCAL_ASR_FALLBACK_REASON } from './localAsrFallbackReason';

const CONSOLE_PREFIX = '[local-asr';

let timeline = [];
let startedAt = null;

/**
 * Whether a value renders usefully inline in the console summary. Objects and arrays
 * are passed to the console as a second argument instead so they stay expandable.
 * @param {*} value
 * @returns {boolean}
 */
function isScalar(value) {
  return value === null || ['string', 'number', 'boolean', 'undefined'].includes(typeof value);
}

/**
 * Formats one event as a single scannable console line, e.g.
 * `[local-asr +1240ms] assets.ready version=vosk-v3 fetched=3`.
 * @param {number} at
 * @param {string} event
 * @param {object} detail
 * @returns {{line: string, extra: object|null}}
 */
function formatEvent(at, event, detail) {
  const scalars = [];
  const extra = {};
  let hasExtra = false;

  Object.entries(detail).forEach(([key, value]) => {
    if (isScalar(value)) {
      if (value !== undefined) {
        scalars.push(`${key}=${value}`);
      }
      return;
    }
    extra[key] = value;
    hasExtra = true;
  });

  const summary = scalars.length > 0 ? ` ${scalars.join(' ')}` : '';
  return {
    line: `${CONSOLE_PREFIX} +${at}ms] ${event}${summary}`,
    extra: hasExtra ? extra : null,
  };
}

/**
 * Appends an event to the timeline and mirrors it to the console.
 * @param {string} event Dot-namespaced event name.
 * @param {object} [detail] Scalar fields render inline; objects are logged expandably.
 * @param {'log'|'info'|'warn'} level Console method for the mirror.
 * @returns {void}
 */
function record(event, detail, level) {
  if (startedAt === null) {
    startedAt = Date.now();
  }
  const at = Date.now() - startedAt;
  const entry = { at, event, ...detail };
  timeline.push(entry);

  // The console mirror is best-effort — the timeline entry above is the durable record.
  // Every caller is on an asset, init, or decode path, so a missing or throwing console
  // must never propagate: matching the guarantee LocalPlacementAsrHandler._warn makes.
  if (typeof console === 'undefined') {
    return;
  }
  const write = console[level];
  if (typeof write !== 'function') {
    return;
  }

  try {
    const { line, extra } = formatEvent(at, event, detail);
    if (extra) {
      write(line, extra);
    } else {
      write(line);
    }
  } catch (error) {
    // Logging must never affect the ASR lifecycle.
  }
}

/**
 * Records a normal lifecycle milestone.
 * @param {string} event
 * @param {object} [detail]
 * @returns {void}
 */
export function recordLocalAsrLifecycle(event, detail = {}) {
  record(event, detail, 'log');
}

/**
 * Records a lifecycle failure or degradation. Flagged in the timeline so a reader (or a
 * query over the flushed objects) can find the problems without knowing every event name.
 *
 * Deliberately does not flush: a failing preload can emit one of these per asset, and
 * writing the whole growing timeline each time would cost more than the old
 * one-object-per-event scheme it replaces. Every failure is picked up by the next
 * checkpoint flush, and the pre-activity checkpoint (preload settle) always runs.
 *
 * `expected` keeps a real degradation off console.warn when its cause is a known normal
 * state (a story whose LM graphs are not published yet, an integrity check that has
 * nothing to compare against). Those still carry `failure: true` — the decode really is
 * downgraded — so the flag is recorded too, letting a query separate "degraded, cause
 * understood" from "degraded, look at this".
 * @param {string} event
 * @param {object} [detail]
 * @param {{expected?: boolean}} [options]
 * @returns {void}
 */
export function recordLocalAsrFailure(event, detail = {}, { expected = false } = {}) {
  const entry = expected ? { ...detail, failure: true, expected: true } : { ...detail, failure: true };
  record(event, entry, expected ? 'info' : 'warn');
}

/**
 * Writes the accumulated timeline to S3 as one object.
 *
 * Called at checkpoints rather than per event: preload settle (the pre-activity story,
 * keyed by user since no activity exists yet), runtime init settle, activity start (the
 * same pre-activity story, now keyed by the activity), and activity end (which adds the
 * in-activity decode-path degradations). The timeline is cumulative and never cleared
 * for the session, so the last flush of a session is the complete record.
 *
 * A multi-activity session therefore writes activities 1..N's events into activity N's
 * object. Attribute an event to its activity by the first `runtime.activityEnded` marker
 * that follows it: that marker carries the `activityId` that just finished.
 *
 * `events` is a copy because the timeline keeps growing after the flush: anything holding
 * the payload rather than serializing it immediately would otherwise see events that
 * postdate the checkpoint it captured. Both current consumers do serialize synchronously
 * (logSessionInfo stringifies before its first await, and the debug-menu interceptor
 * stringifies into its buffer), so this guards the next consumer, and jest's recorded
 * mock arguments, rather than a live defect.
 * `activityId` pins the S3 key instead of letting logSessionInfo read the live
 * getUserData().activityId at call time. The activity-end checkpoint runs while the
 * session is being torn down, so the live value can already have advanced to the next
 * activity or gone missing (which falls back to the hex-of-userId key) — either way the
 * object stops being keyed by the activity whose events it holds, which is the whole
 * premise of attributing events via the trailing `runtime.activityEnded` marker. Callers
 * that flush before an activity exists omit it and keep the user-keyed fallback.
 * @param {string} reason Checkpoint that triggered the flush.
 * @param {object} [options]
 * @param {string} [options.activityId] Pins the S3 key to this activity.
 * @returns {Promise<boolean>}
 */
export function flushLocalAsrLifecycle(reason, { activityId } = {}) {
  if (timeline.length === 0) {
    return Promise.resolve(true);
  }
  return logSessionInfo(LOCAL_ASR_LIFECYCLE_LOG_TYPE, {
    reason,
    elapsedMs: timeline[timeline.length - 1].at,
    eventCount: timeline.length,
    events: timeline.slice(),
  }, activityId || null);
}

/**
 * The accumulated timeline (copy). Exposed for the Debug Menu and tests.
 * @returns {object[]}
 */
export function getLocalAsrLifecycle() {
  return timeline.slice();
}

/** Test-only: drops the module-scoped timeline shared across tests. */
export function resetLocalAsrLifecycleForTests() {
  timeline = [];
  startedAt = null;
}
